export const requestType = {
    addUser: "ADD_USER",
    authUser: "AUTH_USER_V2",
    logoutUser: "LOGOUT_USER",
    updateUser: "UPDATE_USER",
    changeUserPassword: "CHANGE_USER_PASSWORD",
    getUserDetails: "GET_USER_DETAILS",
    // Verified against the BE clone (user.php:4272 → getUserDetail($id, true, $rawData)). Payload is
    // unchanged (`user_uid` only) and the base query, columns, `permission_details`, all `is_*_user`
    // flags, `permissions` and `user_market_details` are byte-identical — so it is safe to keep
    // writing the response into localStorage `appUser`.
    // The flag only re-sources `travel_agency_details` + `store_details` from the local
    // users_to_accounts → salesforce_accounts join instead of travel_agent_companies plus TWO live
    // Salesforce API calls (getStoreDetailsFromSalesforceByAbta/ByPhone) — so V2 is also faster and
    // has one less external dependency. Custom-logo precedence is preserved on the BE
    // (buildTravelAgencyDetailsV2 keeps the uploaded logo and exposes `logo_original`).
    // ⚠️ One real delta: `store_details.email` is hardcoded NULL in V2 (V1 read Salesforce
    // `Email__c`). Only consumer is TuiBeforeStart.jsx, which already ternary-guards it.
    getUserDetailsV2: "GET_USER_DETAILS_V2",
    checkUserSession: "CHECK_USER_SESSION",
    getTravelAgentCompanies: "GET_TRAVEL_AGENT_COMPANIES",
    getTravelAgentCompaniesV2: "GET_TRAVEL_AGENT_COMPANIES_V2",
    getAgencyHqV2: "GET_AGENCY_HQ_V2",
    // SPIKE (spike/unified-account-nodes-41829497, BE ADR 0001): the ROLLUP -> CONSORTIA ->
    // AFFILIATION -> AGENCY tree behind the brand selector's "Account Groups" browser. Nodes are
    // addressed by `account_ref` (ROL:/CON:/AFF:/AGY:) — never send one as `agency_id`.
    getAccountNodesV2: "GET_ACCOUNT_NODES_V2",
    getFavoriteTravelAgentsV2: "GET_FAVORITE_TRAVEL_AGENTS_V2",
    addFavoriteTravelAgentV2: "ADD_FAVORITE_TRAVEL_AGENT_V2",
    removeFavoriteTravelAgentV2: "REMOVE_FAVORITE_TRAVEL_AGENT_V2",
    getFavoriteTravelAgents: "GET_FAVORITE_TRAVEL_AGENTS",
    addFavoriteTravelAgent: "ADD_FAVORITE_TRAVEL_AGENT",
    removeFavoriteTravelAgent: "REMOVE_FAVORITE_TRAVEL_AGENT",
    forgotPassword: "FORGOT_PASSWORD",
    // Royal Momentum and Royal Bridge each get their own endpoint so the backend
    // knows which product's reset link to email. MRG and CMS stay on FORGOT_PASSWORD.
    forgotPasswordMomentum: "FORGOT_PASSWORD_MOMENTUM",
    forgotPasswordBridge: "FORGOT_PASSWORD_BRIDGE",
    addCampaignBanner: "ADD_CAMPAIGN_BANNER",
    updateCampaignBanner: "UPDATE_CAMPAIGN_BANNER",
    deleteCampaignBanner: "DELETE_CAMPAIGN_BANNER",
    getCampaignBanners: "GET_CAMPAIGN_BANNERS",
    uploadFile: "UPLOAD_FILE",
    getItineraryPorts: "GET_ITINERARY_PORTS_BY_REGION",
    getItineraryRegions: "GET_ITINERARY_REGIONS",
    getMatchItineraries: "GET_MATCH_ITINERARIES",
    addCustomerHolidays: "ADD_CUSTOMER_HOLIDAYS",
    getCustomerHolidayItineraries: "GET_CUSTOMER_HOLIDAY_ITINERARIES",
    // customer.php V2 set (this one at customer.php:633). Verified against the BE clone: the source
    // comment states the WHERE logic is identical and still keyed on `user_uid`, and that `agency_id`
    // is "accepted for consistency + echo-back only; ... does NOT scope the query". So the payload
    // needs no change at all — only the request_type. The single behavioural change is that the
    // agency name/logo now come from salesforce_accounts via users_to_accounts.
    getCustomerHolidayItinerariesV2: "GET_CUSTOMER_HOLIDAY_ITINERARIES_V2",
    getItinerayDetails: "GET_ITINERARY_DETAILS",
    addItinerariesToExistingCustomer: "ADD_ITINERARIES_TO_EXISTING_CUSTOMER_HOLIDAY",
    deleteCustomerHolidayItinerary: "DELETE_CUSTOMER_HOLIDAY_ITINERARY",
    getCustomerHolidayLinks: "GET_CUSTOMER_HOLIDAY_LINKS",
    addFocusHolidays: "ADD_FOCUS_HOLIDAYS",
    deleteFocusHolidays: "DELETE_FOCUS_HOLIDAY",
    getFocusHolidays: "GET_FOCUS_HOLIDAYS",
    updateFocusHolidays: "UPDATE_FOCUS_HOLIDAY",
    getLinkDetails: "GET_LINK_DETAILS",
    getLinkDetailsLegend: "GET_LINK_DETAILS_LEGEND",
    // Also customer.php (getLinkDetailsV2 at :1855, getLinkDetailsLegendV2 at :2136 which just forces
    // link_code='legend' and delegates). Same verification as above — identical link_code/user_uid
    // WHERE logic, `agency_id` echo-back only and explicitly NOT used to scope the query, agency
    // name/logo re-sourced from salesforce_accounts. Both keep V1's no-session-check routing
    // (app_endpoints.php:1948/1954), which matters: these serve the PUBLIC microsite routes.
    getLinkDetailsV2: "GET_LINK_DETAILS_V2",
    getLinkDetailsLegendV2: "GET_LINK_DETAILS_LEGEND_V2",
    getPublicItineraryDetails: "GET_PUBLIC_ITINERARY_DETAILS",
    getPublicItineraryDetailsLegend: "GET_PUBLIC_ITINERARY_DETAILS_LEGEND",
    updateCustomerHolidayPurchaseStatus: "UPDATE_CUSTOMER_HOLIDAY_PURCHASE_STATUS",
    resetPassword: "RESET_PASSWORD",
    sendEnquiry: "SEND_ENQUIRY",
    getHolidayAnalytics: "GET_HOLIDAY_ANALYTICS",
    getItineraryShipStatus: "GET_ITINERARY_SHIP_STATUS",
    getDataLastUpdated: "GET_DATA_LAST_UPDATED",
    getLeaderboardAnalytics: "GET_LEADERBOARD_ANALYTICS",
    // ⚠️ Constant only — the CMS call sites stay on V1, now confirmed against the BE clone
    // (cms.php:5000). V2 is not capable of serving this screen:
    //   1. It takes ONE agency. Scope comes from _resolveV2AgencyIds, which does
    //      `(int)trim($rawData['agency_id'])` — a single int cast. The CMS partner picker is
    //      multi-select and sends a comma-joined list ("12,34,56"), which PHP would truncate to `12`.
    //      Unlike `year`, there is no explode() for agency anywhere in the method.
    //   2. Wrong id space. The picker is filled from getTravelAgentCompanies (V1, legacy
    //      `travel_agent_companies`), so `partner.uid` is a legacy uid — V2 matches
    //      `uta.universal_agent_id` (= agency_id_no_r) and applies it as a hard filter
    //      (`AND uta.universal_agent_id IN (...)`), with the `[-1]` sentinel when unresolved.
    //   3. Shared payload. CMSAnalytics sends one `apiParams` object to all ~20 ANALYTICS_CONFIG
    //      endpoints in a single loop; the rest still expect `travel_agency_uid`.
    // Either failure mode returns an empty leaderboard silently, not an error. Migrating needs the
    // picker moved to getTravelAgentCompaniesV2 AND BE support for a multi-agency payload.
    getLeaderboardAnalyticsV2: "GET_LEADERBOARD_ANALYTICS_V2",
    getItinerayShips: "GET_ITINERARY_SHIPS",
    getAvailableMonths: "GET_AVAILABLE_ITINERARY_MONTH_BY_YEAR",
    getAvailableDays: "GET_AVAILABLE_ITINERARY_DAY_BY_YEAR_MONTH",
    getAvailableItineraryYears: "GET_AVAILABLE_ITINERARY_YEARS",
    sendCustomerLikeItineraries: "SEND_CUSTOMER_LIKE_ITINERARIES",
    closeItinerary: "CLOSE_ITINERARY",
    setItineraryAsBooked: "SET_ITINERARY_AS_BOOKED",
    getBookedCruises: "GET_BOOKED_CRUISES",
    setUserTracking: "SET_USER_TRACKING",
    getUserTracking: "GET_USER_TRACKING",
    //analytics
    getHolidayAnalyticsTotalUsersSignup: "GET_HOLIDAY_ANALYTICS_TOTAL_USERS_SIGNUP",

    getTotalProposalCreated: "GET_HOLIDAY_ANALYTICS_TOTAL_PROPOSAL_CREATED",
    getTotalItinerariesSelected: "GET_HOLIDAY_ANALYTICS_TOTAL_ITINERARIES_SELECTED",
    getTotalProposalsCreatedByMonth: "GET_HOLIDAY_ANALYTICS_TOTAL_PROPOSALS_CREATED_BY_MONTH",
    getTotalAgentsCreatingProposals: "GET_HOLIDAY_ANALYTICS_TOTAL_AGENTS_CREATING_PROPOSALS",
    getTotalUsersSignupByMonth: "GET_HOLIDAY_ANALYTICS_TOTAL_USERS_SIGNUP_BY_MONTH",
    getTotalRegionsOnSelectedItineraries:
        "GET_HOLIDAY_ANALYTICS_TOTAL_REGIONS_ON_SELECTED_ITINERARIES",
    getTotalChosenProposalByFamilyType:
        "GET_HOLIDAY_ANALYTICS_TOTAL_CHOSEN_PROPOSAL_BY_FAMILY_TYPE",
    getAverageProposalsPerAgent: "GET_HOLIDAY_ANALYTICS_AVERAGE_PROPOSALS_PER_AGENT",
    getTotalTop10SelectedItineraries: "GET_HOLIDAY_ANALYTICS_TOTAL_TOP_10_SELECTED_ITINERARIES",
    getSailingDatesByQuarterOnSelectedItineraries:
        "GET_HOLIDAY_ANALYTICS_SAILING_DATES_BY_QUARTER_ON_SELECTED_ITINERARIES",
    getTotalItineraryLikesFromCustomers:
        "GET_HOLIDAY_ANALYTICS_TOTAL_ITINERARY_LIKES_FROM_CUSTOMERS",
    getAverageLikesPerCustomer: "GET_HOLIDAY_ANALYTICS_AVERAGE_LIKES_PER_CUSTOMER",
    getMyRoyalGenieBookedCruises: "GET_HOLIDAY_ANALYTICS_MY_ROYAL_GENIE_BOOKED_CRUISES",
    getProposalsUsingGenieAI: "GET_HOLIDAY_ANALYTICS_PROPOSALS_USING_GENIE_AI",
    getBookedCruisesThatUsedGenieAI: "GET_HOLIDAY_ANALYTICS_BOOKED_CRUISES_THAT_USED_GENIE_AI",
    getGenieAIUsage: "GET_HOLIDAY_ANALYTICS_GENIE_AI_USAGE",
    getHolidayAnalyticsAaverageProposalCreationToBookingTime:
        "GET_HOLIDAY_ANALYTICS_AVERAGE_PROPOSAL_CREATION_TO_BOOKING_TIME",
    likeItinerary: "LIKE_ITINERARY",
    unlikeItinerary: "UNLIKE_ITINERARY",
    // Commercial Performance
    getTotalBooking: "GET_TOTAL_BOOKING",
    // NOTE: these two V1 constants point at the *_WITH_TARGET endpoints, not GET_TOTAL_REVENUE /
    // GET_TOTAL_PASSENGERS. Their V2 counterparts are getTotalRevenueV2 / getTotalPassengersV2
    // below — do NOT reuse getTotalRevenueWithTargetV2 here, that one only accepts year/quarter.
    getTotalRevenue: "GET_TOTAL_REVENUE_WITH_TARGET",
    getTotalPassengers: "GET_TOTAL_PASSENGERS_WITH_TARGET",
    getTotalSailing: "GET_TOTAL_SAILING",
    getProductMix: "GET_PRODUCT_MIX",
    getStateroomMix: "GET_STATEROOM_MIX",
    getTop10Cruises: "GET_TOP_10_CRUISES",
    getBookingToDeparture: "GET_BOOKING_TO_DEPARTURE",
    getStorePerformance: "GET_STORE_PERFORMANCE",
    getShipMix: "GET_SHIP_MIX",
    getRegions: "GET_REGIONS",
    getWeekRange: "GET_WEEK_RANGE",
    // Commercial Performance — V2 (used by both Royal Momentum and Royal Bridge).
    // Revenue/Passengers reuse getTotalRevenueWithTargetV2 / getTotalPassengersWithTargetV2.
    getTotalBookingV2: "GET_TOTAL_BOOKING_V2",
    // Dedicated revenue/passengers V2 — unlike getTotalRevenueWithTargetV2 / getTotalPassengersWithTargetV2
    // (year + quarter only) these accept type = year | month | date_range, so month/week/date-range
    // filters return the actual period instead of the full year. Use these for the figure alone;
    // use the WITH_TARGET pair when the target/target_achieved fields are needed.
    getTotalRevenueV2: "GET_TOTAL_REVENUE_V2",
    getTotalPassengersV2: "GET_TOTAL_PASSENGERS_V2",
    getProductMixV2: "GET_PRODUCT_MIX_V2",
    getStateroomMixV2: "GET_STATEROOM_MIX_V2",
    getTop10CruisesV2: "GET_TOP_10_CRUISES_V2",
    getStorePerformanceV2: "GET_STORE_PERFORMANCE_V2",
    getShipMixV2: "GET_SHIP_MIX_V2",
    getTotalSailingV2: "GET_TOTAL_SAILING_V2",
    getBookingToDepartureV2: "GET_BOOKING_TO_DEPARTURE_V2",
    // Agent Engagement - Royal Genie
    getTotalAgentsSignedUp: "GET_TOTAL_AGENTS_SIGNED_UP",
    getTotalAgentsSignedUpV2: "GET_TOTAL_AGENTS_SIGNED_UP_V2",
    getAgentsSignedUpByMonth: "GET_AGENTS_SIGNED_UP_BY_MONTH",
    getAgentsSignedUpByMonthV2: "GET_AGENTS_SIGNED_UP_BY_MONTH_V2",
    getProposalsGeneratedByMonth: "GET_PROPOSALS_GENERATED_BY_MONTH",
    getProposalsGeneratedByMonthV2: "GET_PROPOSALS_GENERATED_BY_MONTH_V2",
    getTotalProposalsGenerated: "GET_TOTAL_PROPOSALS_GENERATED",
    getProductMixGenieProposals: "GET_PRODUCT_MIX_GENIE_PROPOSALS",
    getTop10ItinerariesGenieProposals: "GET_TOP_10_ITINERARIES_GENIE_PROPOSALS",
    getStoreBreakdown: "GET_STORE_BREAKDOWN",
    getStoreBreakdownV2: "GET_STORE_BREAKDOWN_V2",
    // Agent Engagement - Club Learn
    getFocusModules: "GET_FOCUS_MODULES",
    getFocusModulesV2: "GET_FOCUS_MODULES_V2",
    getMyRoyalGuruTotals: "GET_MY_ROYAL_GURU_TOTALS",
    getMyRoyalGuruTotalsV2: "GET_MY_ROYAL_GURU_TOTALS_V2",
    getStoresWithRoyalGuru: "GET_STORES_WITH_ROYAL_GURU",
    getStoresWithRoyalGuruV2: "GET_STORES_WITH_ROYAL_GURU_V2",
    getStoresWithoutRoyalGuru: "GET_STORES_WITHOUT_ROYAL_GURU",
    getStoresWithoutRoyalGuruV2: "GET_STORES_WITHOUT_ROYAL_GURU_V2",
    getTotalModulesCompleted: "GET_TOTAL_MODULES_COMPLETED",
    getTotalModulesCompletedV2: "GET_TOTAL_MODULES_COMPLETED_V2",
    getClubLearnModulesCompletedByMonth: "GET_CLUB_LEARN_MODULES_COMPLETED_BY_MONTH",
    getClubLearnModulesCompletedByMonthV2: "GET_CLUB_LEARN_MODULES_COMPLETED_BY_MONTH_V2",
    getTopStoreGuru: "GET_TOP_STORE_GURU",
    getTopStoreGuruV2: "GET_TOP_STORE_GURU_V2",
    // Agent Engagement - Upper Deck
    getTotalBookingsRedeemed: "GET_TOTAL_BOOKINGS_REDEEMED",
    getTotalBookingsRedeemedV2: "GET_TOTAL_BOOKINGS_REDEEMED_V2",
    getBookingsRedeemedByMonth: "GET_TOTAL_BOOKINGS_REDEEMED_BY_MONTH",
    getBookingsRedeemedByMonthV2: "GET_TOTAL_BOOKINGS_REDEEMED_BY_MONTH_V2",
    // Upper Deck's own totals. The tab used to read the two generic constants above — the agents one
    // shared with Royal Genie — but the BE now exposes Upper-Deck-scoped versions. Payload is the
    // standard V2 agency shape (`agency_id`), never a store id.
    getUpperDeckTotalAgentsSignedUpV2: "GET_UPPER_DECK_TOTAL_AGENTS_SIGNED_UP_V2",
    getUpperDeckTotalBookingsRedeemedV2: "GET_UPPER_DECK_TOTAL_BOOKINGS_REDEEMED_V2",
    // Pipeline
    getTotalAgentsUsingGenieProposals: "GET_TOTAL_AGENTS_USING_GENIE_PROPOSALS",
    getStoresNotUsingMyRoyalGenie: "GET_STORES_NOT_USING_MY_ROYAL_GENIE",
    getSailingDatesItinerariesSelectedProposals: "GET_SAILING_DATES_ITINERARIES_SELECTED_PROPOSALS",
    // Pipeline — V2 (used by both the Royal Momentum and Royal Bridge Pipeline pages)
    getTotalProposalsGeneratedV2: "GET_TOTAL_PROPOSALS_GENERATED_V2",
    getTotalAgentsUsingGenieProposalsV2: "GET_TOTAL_AGENTS_USING_GENIE_PROPOSALS_V2",
    getStoresNotUsingMyRoyalGenieV2: "GET_STORES_NOT_USING_MY_ROYAL_GENIE_V2",
    getProductMixGenieProposalsV2: "GET_PRODUCT_MIX_GENIE_PROPOSALS_V2",
    getTop10ItinerariesGenieProposalsV2: "GET_TOP_10_ITINERARIES_GENIE_PROPOSALS_V2",
    getSailingDatesItinerariesSelectedProposalsV2:
        "GET_SAILING_DATES_ITINERARIES_SELECTED_PROPOSALS_V2",
    // Meeting Hub
    searchMeetingAttendees: "SEARCH_MEETING_ATTENDEES",
    // V2 attendee search — BE split the single V1 endpoint into three: INTERNAL (email LIKE
    // '%@rccl.com'), EXTERNAL (the inverse), and a salesforce_contacts-backed variant with
    // pagination + total_meetings per contact. All three take `agency_id` (optional; when absent
    // no agency restriction is applied — there is no implicit user_uid fallback).
    // Not wired to any component yet: the V1 constant has no call site either.
    searchMeetingInternalAttendeesV2: "SEARCH_MEETING_INTERNAL_ATTENDEES_V2",
    searchMeetingExternalAttendeesV2: "SEARCH_MEETING_EXTERNAL_ATTENDEES_V2",
    searchMeetingAttendeesV2: "SEARCH_MEETING_ATTENDEES_V2",
    addMeeting: "ADD_MEETING",
    addMeetingV2: "ADD_MEETING_V2",
    getMeetings: "GET_MEETINGS",
    getMeetingsV2: "GET_MEETINGS_V2",
    getMeetingDetail: "GET_MEETING_DETAIL",
    getMeetingSummary: "GET_MEETING_SUMMARY",
    getMeetingSummaryExternal: "GET_MEETING_SUMMARY_EXTERNAL",
    saveMeetingSummary: "SAVE_MEETING_SUMMARY",
    saveMeetingSummaryAttachments: "SAVE_MEETING_SUMMARY_ATTACHMENTS",
    // meetings.php V2 set — NOT in the Postman export (its Meetings folder only carries ADD_MEETING_V2,
    // GET_MEETINGS_V2 and the three attendee-search V2s), so no spec was available. On the BE these
    // are the same methods called with a v2 flag (`getMeetingSummary(..., true)` etc.), and the FE
    // payloads carry no agency key — they are keyed by `meeting_uid` — so nothing was renamed.
    // getMeetingDetailV2 has no call site: the V1 constant has never been used either (the meeting
    // detail screen reads getMeetingSummary instead). Added for completeness.
    getMeetingDetailV2: "GET_MEETING_DETAIL_V2",
    getMeetingSummaryV2: "GET_MEETING_SUMMARY_V2",
    getMeetingSummaryExternalV2: "GET_MEETING_SUMMARY_EXTERNAL_V2",
    saveMeetingSummaryV2: "SAVE_MEETING_SUMMARY_V2",
    saveMeetingSummaryAttachmentsV2: "SAVE_MEETING_SUMMARY_ATTACHMENTS_V2",
    saveMeetingSummaryAgenda: "SAVE_MEETING_SUMMARY_AGENDA",
    deleteMeetingAttachment: "DELETE_MEETING_ATTACHMENT",
    deleteMeeting: "DELETE_MEETING",
    getCountries: "GET_COUNTRIES",
    // Royal Momentum - Performance vs Target
    getOverrideTiers: "GET_OVERRIDE_TIERS",
    // V2 exists but is a stub (returns a fixed 4-tier table, no DB query). The Override Tiers UI is
    // already served by getCommercialTargetV2 — see royalBridgePlansAndTargetsApi.getOverrideTiers.
    // Kept here for completeness; confirm with BE which of the two supersedes the other.
    getOverrideTiersV2: "GET_OVERRIDE_TIERS_V2",
    // Royal Momentum - Plans & Goals
    getPlans: "GET_PLANS",
    getPlansV2: "GET_PLANS_V2",
    // Progress Tracker (the "Current Goals" tab). One call per category_plan; returns each goal's
    // current_value, target and a server-computed target_percentage. GET_PLANS is still what the
    // "Edit Goals" tab needs — it alone carries plan_uid, item_target_uid and the not-yet-targeted
    // items that feed the Add Goal dropdown.
    getCurrentGoals: "GET_CURRENT_GOALS",
    savePlanTarget: "SAVE_PLAN_TARGET",
    savePlanTargetV2: "SAVE_PLAN_TARGET_V2",
    deletePlanTarget: "DELETE_PLAN_TARGET",
    deletePlanTargetV2: "DELETE_PLAN_TARGET_V2",
    // Royal Momentum - Agency Profile
    getAgencyProfile: "GET_AGENCY_PROFILE",
    getAgencyProfileDetail: "GET_AGENCY_PROFILE_DETAIL",
    // ⚠️ getAgencyOverview has NO V2 counterpart in either the BE endpoint list or the Postman export,
    // yet it is called from the same Agency Profile screen and also sends travel_agency_uid. So that
    // one page now sends `agency_id` (no-R) to two endpoints and `travel_agency_uid` to overview.
    // Functionally fine — both resolve the same agency — but ask BE whether GET_AGENCY_OVERVIEW_V2
    // exists/is planned, and migrate it together with these two if so.
    getAgencyProfileDetailV2: "GET_AGENCY_PROFILE_DETAIL_V2",
    getAgencyOverview: "GET_AGENCY_OVERVIEW",
    setAgencyProfile: "SET_AGENCY_PROFILE",
    setAgencyProfileV2: "SET_AGENCY_PROFILE_V2",
    downloadAgencyStores: "DOWNLOAD_AGENCY_STORES",
    // Follows the standard V2 shape: `agency_id` (no-R form) replaces `travel_agency_uid`.
    // Sending the V1 payload returns ERROR_MISSING_AGENCY_ID.
    downloadAgencyStoresV2: "DOWNLOAD_AGENCY_STORES_V2",
    addMarketingContact: "ADD_MARKETING_CONTACT",
    // Royal Momentum - Wow Bookings
    getWowBookings: "GET_WOW_BOOKINGS",
    getWowBookingsV2: "GET_WOW_BOOKINGS_V2",
    // Royal Momentum - Growth Priorities
    getGrowthPrioritiesRoyalGuruTarget: "GET_GROWTH_PRIORITIES_ROYAL_GURU_TARGET",
    getGrowthPrioritiesRoyalGuruTargetV2: "GET_GROWTH_PRIORITIES_ROYAL_GURU_TARGET_V2",
    getGrowthPrioritiesRoyalGenieSignup: "GET_GROWTH_PRIORITIES_ROYAL_GENIE_SIGNUP",
    getGrowthPrioritiesRoyalGenieSignupV2: "GET_GROWTH_PRIORITIES_ROYAL_GENIE_SIGNUP_V2",
    getGrowthPrioritiesStoresCertification: "GET_GROWTH_PRIORITIES_STORES_CERTIFICATION",
    getGrowthPrioritiesStoresCertificationV2: "GET_GROWTH_PRIORITIES_STORES_CERTIFICATION_V2",
    getGrowthPrioritiesMeetingsLoggedIn60Days: "GET_GROWTH_PRIORITIES_MEETINGS_LOGGED_IN_60_DAYS",
    getGrowthPrioritiesMeetingsLoggedIn60DaysV2:
        "GET_GROWTH_PRIORITIES_MEETINGS_LOGGED_IN_60_DAYS_V2",
    // Royal Momentum - Tasks & Follow-ups
    getTaskStatistics: "GET_TASK_STATISTICS",
    getTaskStatisticsV2: "GET_TASK_STATISTICS_V2",
    addTask: "ADD_TASK",
    addTaskV2: "ADD_TASK_V2",
    updateTask: "UPDATE_TASK",
    updateTaskV2: "UPDATE_TASK_V2",
    getContacts: "GET_CONTACTS",
    getRciContacts: "GET_RCI_CONTACTS",
    getAgencyKamVisit: "GET_AGENCY_KAM_VISIT",
    getAgencyKamVisitV2: "GET_AGENCY_KAM_VISIT_V2",
    getListMeetingsByAgency: "GET_LIST_MEETINGS_BY_AGENCY",
    getListMeetingsByAgencyV2: "GET_LIST_MEETINGS_BY_AGENCY_V2",
    getTasks: "GET_TASKS",
    getTasksV2: "GET_TASKS_V2",
    // No V1 equivalent is defined in this file — the task-detail endpoint has never been called
    // from the frontend. Added so the V2 set is complete when a detail view is built.
    getTaskDetailV2: "GET_TASK_DETAIL_V2",
    getTasksByMeeting: "GET_TASKS_BY_MEETING",
    // Royal Momentum - Quick Calls
    getCallStatistics: "GET_CALL_STATISTICS",
    getCalls: "GET_CALLS",
    logCall: "LOG_CALL",
    getCallDetail: "GET_CALL_DETAIL",
    updateCall: "UPDATE_CALL",
    deleteCall: "DELETE_CALL",
    // Royal Momentum - Contacts
    getContacts: "GET_CONTACTS",
    getContactsV2: "GET_CONTACTS_V2",
    addContact: "ADD_CONTACT",
    addContactV2: "ADD_CONTACT_V2",
    updateContact: "UPDATE_CONTACT",
    updateContactV2: "UPDATE_CONTACT_V2",
    deleteContact: "DELETE_CONTACT",
    // V2 delete carries `agency_id` like every other V2 write in this set — V1 was keyed by
    // `contact_uid` alone. See royalMomentumContactsApi.deleteContact.
    deleteContactV2: "DELETE_CONTACT_V2",
    getFavoriteContacts: "GET_FAVORITE_CONTACTS",
    getFavoriteContactsV2: "GET_FAVORITE_CONTACTS_V2",
    addFavoriteContact: "ADD_FAVORITE_CONTACT",
    addFavoriteContactV2: "ADD_FAVORITE_CONTACT_V2",
    removeFavoriteContact: "REMOVE_FAVORITE_CONTACT",
    removeFavoriteContactV2: "REMOVE_FAVORITE_CONTACT_V2",
    // Royal Momentum - Commercial Snapshot
    getTotalRevenueYoyPerformance: "GET_TOTAL_REVENUE_YOY_PERFORMANCE",
    getTotalRevenueYoyPerformanceV2: "GET_TOTAL_REVENUE_YOY_PERFORMANCE_V2",
    getTotalRevenueWithTarget: "GET_TOTAL_REVENUE_WITH_TARGET",
    getTotalRevenueWithTargetV2: "GET_TOTAL_REVENUE_WITH_TARGET_V2",
    getTotalPassengersWithTarget: "GET_TOTAL_PASSENGERS_WITH_TARGET",
    getTotalPassengersWithTargetV2: "GET_TOTAL_PASSENGERS_WITH_TARGET_V2",
    getCommercialTarget: "GET_COMMERCIAL_TARGET",
    getCommercialTargetV2: "GET_COMMERCIAL_TARGET_V2",
    getRecentWowBookings: "GET_RECENT_WOW_BOOKINGS",
    getRecentWowBookingsV2: "GET_RECENT_WOW_BOOKINGS_V2",
    // Royal Momentum - Store Visits
    getTotalStoreVisits: "GET_TOTAL_STORE_VISITS",
    getTotalStoreVisitsV2: "GET_TOTAL_STORE_VISITS_V2",
    getTotalPassengersYtd: "GET_TOTAL_PASSENGERS_YTD",
    getTotalPassengersYtdV2: "GET_TOTAL_PASSENGERS_YTD_V2",
    getTotalRevenueYtd: "GET_TOTAL_REVENUE_YTD",
    getTotalRevenueYtdV2: "GET_TOTAL_REVENUE_YTD_V2",
    getTotalStoresNotVisited: "GET_TOTAL_STORES_NOT_VISITED",
    getTotalStoresNotVisitedV2: "GET_TOTAL_STORES_NOT_VISITED_V2",
    getStoreVisitImpact: "GET_STORE_VISIT_IMPACT",
    getStoreVisitImpactV2: "GET_STORE_VISIT_IMPACT_V2",
    getStoreVisitsRevenue: "GET_STORE_VISITS_REVENUE",
    // ⚠️ Half-migrated, on purpose — the slice picks the version from `show`. Used for
    // `show: "upcoming"` (Overview + Your Visits), which sends `agency_id`. The `show: "history"`
    // call sites stay on V1: the V2 twin adds an unconditional `uta.universal_agent_id IN (...)`
    // filter driven by the BE agency resolver (explicit agency_id > implicit-from-user_uid > `[-1]`
    // sentinel), and those payloads carry no agency key, so their lists would narrow to one derived
    // agency or empty out entirely. Per the BE's _tech/api-v2-migration.md the two versions are
    // meant to run in parallel. See royalMomentumStoreVisitsApi.getStoreVisitsRevenue.
    getStoreVisitsRevenueV2: "GET_STORE_VISITS_REVENUE_V2",
    getStoreNotVisited: "GET_STORE_NOT_VISITED",
    getStoreNotVisitedV2: "GET_STORE_NOT_VISITED_V2",
    getStores: "GET_STORES",
    getStoresV2: "GET_STORES_V2",
    addStoreVisit: "ADD_STORE_VISIT",
    addStoreVisitV2: "ADD_STORE_VISIT_V2",
    getTotalUniqueStoreVisits: "GET_TOTAL_UNIQUE_STORE_VISITS",
    getTotalUniqueStoreVisitsV2: "GET_TOTAL_UNIQUE_STORE_VISITS_V2",
    getAverageVisitsPerStore: "GET_AVERAGE_VISITS_PER_STORE",
    getAverageVisitsPerStoreV2: "GET_AVERAGE_VISITS_PER_STORE_V2",
    getStoreWithoutAVisit: "GET_STORE_WITHOUT_A_VISIT",
    getStoreWithoutAVisitV2: "GET_STORE_WITHOUT_A_VISIT_V2",
    getTotalStoreVisitsByMonth: "GET_TOTAL_STORE_VISITS_BY_MONTH",
    getTotalStoreVisitsByMonthV2: "GET_TOTAL_STORE_VISITS_BY_MONTH_V2",
    getStoreVisitImpactOnBookings: "GET_STORE_VISIT_IMPACT_ON_BOOKINGS",
    getStoreVisitImpactOnBookingsV2: "GET_STORE_VISIT_IMPACT_ON_BOOKINGS_V2",
    getCruiseBrandsSold: "GET_CRUISE_BRANDS_SOLD",
    getCruiseBrandsSoldV2: "GET_CRUISE_BRANDS_SOLD_V2",
    getLandBasedBrandsSold: "GET_LAND_BASED_BRANDS_SOLD",
    getLandBasedBrandsSoldV2: "GET_LAND_BASED_BRANDS_SOLD_V2",
    getOceanCruiseBusinessPercentage: "GET_OCEAN_CRUISE_BUSINESS_PERCENTAGE",
    getOceanCruiseBusinessPercentageV2: "GET_OCEAN_CRUISE_BUSINESS_PERCENTAGE_V2",
    getInStorePromotionalOpportunities: "GET_IN_STORE_PROMOTIONAL_OPPORTUNITIES",
    getInStorePromotionalOpportunitiesV2: "GET_IN_STORE_PROMOTIONAL_OPPORTUNITIES_V2",
    getStoreVisitMarketCondition: "GET_STORE_VISIT_MARKET_CONDITION",
    getStoreVisitMarketConditionV2: "GET_STORE_VISIT_MARKET_CONDITION_V2",
    getStoreVisitProgressCompletion: "GET_STORE_VISIT_PROGRESS_COMPLETION",
    getStoreVisitProgressCompletionV2: "GET_STORE_VISIT_PROGRESS_COMPLETION_V2",
    getStoreDetail: "GET_STORE_DETAIL",
    // Keyed by `agency_id` (a single store's own agency id), like the other store endpoints.
    getStoreDetailV2: "GET_STORE_DETAIL_V2",
    getStoreLastVisit: "GET_STORE_LAST_VISIT",
    getStoreRevenueThisYear: "GET_STORE_REVENUE_THIS_YEAR",
    getStoreRevenueThisYearV2: "GET_STORE_REVENUE_THIS_YEAR_V2",
    getStoreRevenueSinceLastVisit: "GET_STORE_REVENUE_SINCE_LAST_VISIT",
    getStoreRevenueSinceLastVisitV2: "GET_STORE_REVENUE_SINCE_LAST_VISIT_V2",
    getStorePaxThisYear: "GET_STORE_PAX_THIS_YEAR",
    getStorePaxThisYearV2: "GET_STORE_PAX_THIS_YEAR_V2",
    getStoreWowBookings: "GET_STORE_WOW_BOOKINGS",
    getStoreWowBookingsV2: "GET_STORE_WOW_BOOKINGS_V2",
    getGroupBookings: "GET_GROUP_BOOKINGS",
    // Payload is `{ user_uid, agency_id }` — no store key, matching the rest of the Pre-Visit set.
    getGroupBookingsV2: "GET_GROUP_BOOKINGS_V2",
    getClubRoyalsMembers: "GET_CLUB_ROYALS_MEMBERS",
    getClubRoyalsMembersV2: "GET_CLUB_ROYALS_MEMBERS_V2",
    getMyRoyalGenieMembers: "GET_MY_ROYAL_GENIE_MEMBERS",
    getMyRoyalGenieMembersV2: "GET_MY_ROYAL_GENIE_MEMBERS_V2",
    getStoreProposalsGenerated: "GET_STORE_PROPOSALS_GENERATED",
    getStoreProposalsGeneratedV2: "GET_STORE_PROPOSALS_GENERATED_V2",
    getStoreTotalProposalsCreated: "GET_STORE_TOTAL_PROPOSALS_CREATED",
    getStoreTotalProposalsCreatedV2: "GET_STORE_TOTAL_PROPOSALS_CREATED_V2",
    getStoreUniqueAgentsUsedGenie: "GET_STORE_UNIQUE_AGENTS_USED_GENIE",
    getStoreUniqueAgentsUsedGenieV2: "GET_STORE_UNIQUE_AGENTS_USED_GENIE_V2",
    getStoreAgentsEnrolledUpperDeck: "GET_STORE_AGENTS_ENROLLED_UPPER_DECK",
    getStoreAgentsEnrolledUpperDeckV2: "GET_STORE_AGENTS_ENROLLED_UPPER_DECK_V2",
    getStoreBookingsClaimedUpperDeck: "GET_STORE_BOOKINGS_CLAIMED_UPPER_DECK",
    getStoreBookingsClaimedUpperDeckV2: "GET_STORE_BOOKINGS_CLAIMED_UPPER_DECK_V2",
    getStoreBookingsClaimedThroughUpperDeck: "GET_BOOKINGS_CLAIMED_THROUGH_UPPER_DECK",
    getStoreBookingsClaimedThroughUpperDeckV2: "GET_BOOKINGS_CLAIMED_THROUGH_UPPER_DECK_V2",
    getStoreBookingsClaimViaUpperDeck: "GET_STORE_BOOKINGS_CLAIM_VIA_UPPER_DECK",
    getStoreBookingsClaimViaUpperDeckV2: "GET_STORE_BOOKINGS_CLAIM_VIA_UPPER_DECK_V2",
    getStoreModulesCompleted: "GET_STORE_MODULES_COMPLETED",
    getStoreModulesCompletedV2: "GET_STORE_MODULES_COMPLETED_V2",
    getStoreRoyalGurus: "GET_STORE_ROYAL_GURUS",
    getStoreRoyalGurusV2: "GET_STORE_ROYAL_GURUS_V2",
    getStoreRoyalGurusList: "GET_ROYAL_GURUS_DATA",
    getStoreRoyalGurusListV2: "GET_ROYAL_GURUS_DATA_V2",
    getStoreFocusModules: "GET_STORE_FOCUS_MODULES",
    getStoreFocusModulesV2: "GET_STORE_FOCUS_MODULES_V2",
    getStoreLastVisit: "GET_STORE_LAST_VISIT",
    getStoreLastVisitV2: "GET_STORE_LAST_VISIT_V2",
    getStoreBookingsRedeemedUpperDeck: "GET_STORE_BOOKINGS_REDEEMED_UPPER_DECK",
    getStoreBookingsRedeemedUpperDeckV2: "GET_STORE_BOOKINGS_REDEEMED_UPPER_DECK_V2",
    getStoreClubLearnModulesCompleted: "GET_STORE_CLUB_LEARN_MODULES_COMPLETED",
    getStoreClubLearnModulesCompletedV2: "GET_STORE_CLUB_LEARN_MODULES_COMPLETED_V2",
    getStoreProductMix: "GET_STORE_PRODUCT_MIX",
    getStoreProductMixV2: "GET_STORE_PRODUCT_MIX_V2",
    getStoreTotalBookings: "GET_STORE_TOTAL_BOOKINGS",
    getStoreTotalBookingsV2: "GET_STORE_TOTAL_BOOKINGS_V2",
    getStoreTotalPassengers: "GET_STORE_TOTAL_PASSENGERS",
    getStoreTotalPassengersV2: "GET_STORE_TOTAL_PASSENGERS_V2",
    getStoreCustomerYearlyTrips: "GET_STORE_CUSTOMER_YEARLY_TRIPS",
    getStoreCustomerYearlyTripsV2: "GET_STORE_CUSTOMER_YEARLY_TRIPS_V2",
    getStoreBookingsAreSailing: "GET_STORE_BOOKINGS_ARE_SAILING",
    getStoreBookingsAreSailingV2: "GET_STORE_BOOKINGS_ARE_SAILING_V2",
    getStoreCustomersCabinTypes: "GET_STORE_CUSTOMERS_CABIN_TYPES",
    getStoreCustomersCabinTypesV2: "GET_STORE_CUSTOMERS_CABIN_TYPES_V2",
    getStoreTopCruises: "GET_STORE_TOP_CRUISES",
    getStoreTopCruisesV2: "GET_STORE_TOP_CRUISES_V2",
    getStoreBookingOverview: "GET_STORE_BOOKING_OVERVIEW",
    getStoreBookingOverviewV2: "GET_STORE_BOOKING_OVERVIEW_V2",
    getStoreBookingToDeparture: "GET_STORE_BOOKING_TO_DEPARTURE",
    getStoreBookingToDepartureV2: "GET_STORE_BOOKING_TO_DEPARTURE_V2",
    getStoreShipMix: "GET_STORE_SHIP_MIX",
    getStoreShipMixV2: "GET_STORE_SHIP_MIX_V2",
    getStoreVisitStepData: "GET_STORE_VISIT_STEP_DATA",
    setStoreVisitStepData: "SET_STORE_VISIT_STEP_DATA",
    updateStoreVisit: "UPDATE_STORE_VISIT",
    getStoreVisitSummary: "GET_STORE_VISIT_SUMMARY",
    getStoreVisitSummaryPublic: "GET_STORE_VISIT_SUMMARY_PUBLIC",
    getShips: "GET_SHIPS",
    // Uploads a file to HubSpot and returns its hosted URL. Sent as multipart
    // FormData rather than JSON — see src/services/uploadAsset.js.
    uploadAssetToHubspot: "UPLOAD_ASSET_TO_HUBSPOT",
    getShipDetails: "GET_SHIP_DETAILS",
    // Agency-scoped ship details: same response shape as GET_SHIP_DETAILS, but
    // returns the content for one agency. Used by the content management
    // preview/edit page, which edits an agency's copy of a ship page.
    getAgencyShipDetails: "GET_AGENCY_SHIP_DETAILS_V2",
    // Writing an agency's ship page. Which one to call is decided by
    // `is_customised` on the GET_AGENCY_SHIP_DETAILS_V2 response: false means the
    // agency is still on the default content and needs its own copy created;
    // true means it already has one to update. See src/services/agencyShip.js.
    createAgencyShip: "CREATE_AGENCY_SHIP_V2",
    updateAgencyShip: "UPDATE_AGENCY_SHIP_V2",
    getDestinationDetails: "GET_DESTINATION_DETAILS",
    getTradeDestinations: "GET_TRADE_DESTINATIONS",
    // Agency-scoped destinations — the replacement for GET_TRADE_DESTINATIONS.
    // Returns the same destination records wrapped in an envelope
    // (`data.destinations`) that adds per-destination `is_customised` /
    // `custom_destination`, plus agency-wide counts.
    getAgencyDestinations: "GET_AGENCY_DESTINATIONS",
    // Writing an agency's destination page, chosen by `is_customised` exactly as
    // the ship pair above. Also like the ship endpoints, the payload replaces the
    // whole record: `data` carries the destination's fields and its complete
    // `items` array, not just the edited blocks. See
    // src/services/agencyDestination.js.
    createAgencyDestination: "CREATE_AGENCY_DESTINATION",
    updateAgencyDestination: "UPDATE_AGENCY_DESTINATION",
    // Bespoke hero banners — a self-authored banner (own image/header/copy/CTA)
    // with no trade-catalogue row behind it. Unlike the ship and destination pairs
    // above these are multi-row: an agency or market may hold several as separate
    // hero options, so CREATE always inserts and there is no "already exists"
    // conflict to switch on. `GET_CUSTOM_BANNERS` lists them including drafts (the
    // CMS listing needs those); `GET_BRIEF_SHIP_DESTINATION_LISTS_V2` surfaces only
    // published ones, as selectable `category: "bespoke"` options.
    // See src/services/heroBannerContent.js.
    createCustomBanner: "CREATE_CUSTOM_BANNER",
    updateCustomBanner: "UPDATE_CUSTOM_BANNER",
    deleteCustomBanner: "DELETE_CUSTOM_BANNER",
    getCustomBanners: "GET_CUSTOM_BANNERS",
    getBrands: "GET_BRANDS",
    getBrandsV2: "GET_BRANDS_V2",
    setStoreVisitProgress: "SET_STORE_VISIT_PROGRESS",
    getStoreVisitProgress: "GET_STORE_VISIT_PROGRESS",
    // Royal Momentum - KAM Visits (placeholder requestTypes — confirm/replace with real backend values)
    getKamTotalBookings: "GET_KAM_TOTAL_BOOKINGS",
    getKamRevenueThisYear: "GET_KAM_REVENUE_THIS_YEAR",
    getKamTotalPassengers: "GET_KAM_TOTAL_PASSENGERS",
    getKamCustomerYearlyTrips: "GET_KAM_CUSTOMER_YEARLY_TRIPS",
    getKamBookingsAreSailing: "GET_KAM_BOOKINGS_ARE_SAILING",
    getKamCustomersCabinTypes: "GET_KAM_CUSTOMERS_CABIN_TYPES",
    getKamTopCruises: "GET_KAM_TOP_CRUISES",
    getKamBookingOverview: "GET_KAM_BOOKING_OVERVIEW",
    getKamBookingToDeparture: "GET_KAM_BOOKING_TO_DEPARTURE",
    getKamShipMix: "GET_KAM_SHIP_MIX",
    getKamTotalProposalsCreated: "GET_KAM_TOTAL_PROPOSALS_CREATED",
    getKamUniqueAgentsUsedGenie: "GET_KAM_UNIQUE_AGENTS_USED_GENIE",
    getKamAgentsEnrolledUpperDeck: "GET_KAM_AGENTS_ENROLLED_UPPER_DECK",
    getKamBookingsClaimedUpperDeck: "GET_KAM_BOOKINGS_CLAIMED_UPPER_DECK",
    getKamBookingsClaimedThroughUpperDeck: "GET_KAM_BOOKINGS_CLAIMED_THROUGH_UPPER_DECK",
    getKamBookingsClaimViaUpperDeck: "GET_KAM_BOOKINGS_CLAIM_VIA_UPPER_DECK",
    getKamModulesCompleted: "GET_KAM_MODULES_COMPLETED",
    getKamRoyalGurus: "GET_KAM_ROYAL_GURUS",
    getKamRoyalGurusList: "GET_KAM_ROYAL_GURUS_LIST",
    getKamFocusModules: "GET_KAM_FOCUS_MODULES",
    getKamVisitProgress: "GET_KAM_VISIT_PROGRESS",
    setKamVisitProgress: "SET_KAM_VISIT_PROGRESS",
    setKamVisitStepData: "SET_KAM_VISIT_STEP_DATA",
    getKamVisitStepData: "GET_KAM_VISIT_STEP_DATA",
    updateKamVisit: "UPDATE_KAM_VISIT",
    getKamVisit: "GET_KAM_VISIT",
    getKamVisitV2: "GET_KAM_VISIT_V2",
    getKamVisitPublic: "GET_KAM_VISIT_PUBLIC",
    getKamVisitPublicV2: "GET_KAM_VISIT_PUBLIC_V2",
    getKamVisitSummary: "GET_KAM_VISIT_SUMMARY",
    getKamVisitSummaryPublic: "GET_KAM_VISIT_SUMMARY_PUBLIC",
    getKamVisits: "GET_KAM_VISITS",
    getKamVisitsV2: "GET_KAM_VISITS_V2",
    addKamVisit: "ADD_KAM_VISIT",
    addKamVisitV2: "ADD_KAM_VISIT_V2",
    getKamNextSteps: "GET_KAM_NEXT_STEPS",
    getKamNextStepsV2: "GET_KAM_NEXT_STEPS_V2",
    // KAM-specific counterparts to GET_TOTAL_STORE_VISITS_V2 / GET_STORE_VISIT_IMPACT_V2, which the
    // KAM Overview used to borrow. Same payload as those; V2-only, there is no V1 to fall back to.
    getTotalKamVisitsV2: "GET_TOTAL_KAM_VISITS_V2",
    getKamVisitImpactV2: "GET_KAM_VISIT_IMPACT_V2",
    getTotalKamNotVisitedV2: "GET_TOTAL_KAM_NOT_VISITED_V2",
    getStoreVisitGenieUsage: "GET_STORE_VISIT_GENIE_USAGE",
    getStoreVisitBooking: "GET_STORE_VISIT_BOOKING",
    getStoreVisitUpperDeckParticipation: "GET_STORE_VISIT_UPPER_DECK_PARTICIPATION",
    // ⚠️ NOT a drop-in replacement for the three V1 constants above — see RoyalMomentumVisitStep6.jsx.
    // The V2 endpoints require `agency_id` and drop `store_user_uid` entirely, so they report the
    // whole affiliate group instead of the single store being visited. Repointing Step 6 to these
    // would silently change what the screen measures. Blocked pending BE adding store-level scope.
    getStoreVisitGenieUsageV2: "GET_STORE_VISIT_GENIE_USAGE_V2",
    getStoreVisitBookingV2: "GET_STORE_VISIT_BOOKING_V2",
    getStoreVisitUpperDeckParticipationV2: "GET_STORE_VISIT_UPPER_DECK_PARTICIPATION_V2",
    // Royal Momentum - Content Management
    getBriefHeroBanner: "GET_BRIEF_HERO_BANNER",
    getBriefHeroBannerV2: "GET_BRIEF_HERO_BANNER_V2",
    setBriefHeroBanner: "SET_BRIEF_HERO_BANNER",
    setBriefHeroBannerV2: "SET_BRIEF_HERO_BANNER_V2",
    getShipDestinationLists: "GET_BRIEF_SHIP_DESTINATION_LISTS",
    // V2 takes the same payload as V1 (module + user_uid). This list is global rather
    // than agency-scoped, so there is no travel_agency_uid -> agency_id rename to make.
    getShipDestinationListsV2: "GET_BRIEF_SHIP_DESTINATION_LISTS_V2",
    getBriefFocusShips: "GET_BRIEF_FOCUS_SHIPS",
    getBriefFocusShipsV2: "GET_BRIEF_FOCUS_SHIPS_V2",
    setBriefFocusShips: "SET_BRIEF_FOCUS_SHIPS",
    setBriefFocusShipsV2: "SET_BRIEF_FOCUS_SHIPS_V2",
    getBriefFocusDestinations: "GET_BRIEF_FOCUS_DESTINATIONS",
    getBriefFocusDestinationsV2: "GET_BRIEF_FOCUS_DESTINATIONS_V2",
    setBriefFocusDestinations: "SET_BRIEF_FOCUS_DESTINATIONS",
    setBriefFocusDestinationsV2: "SET_BRIEF_FOCUS_DESTINATIONS_V2",
    setBriefCampaign: "SET_BRIEF_CAMPAIGN",
    setBriefCampaignV2: "SET_BRIEF_CAMPAIGN_V2",
    updateBriefCampaign: "UPDATE_BRIEF_CAMPAIGN",
    deleteBriefCampaign: "DELETE_BRIEF_CAMPAIGN",
    getBriefCampaign: "GET_BRIEF_CAMPAIGN",
    getBriefCampaignV2: "GET_BRIEF_CAMPAIGN_V2",
    // Counts for the market-level ("all accounts") save confirm dialogue: how many agencies in
    // the market would receive the content, and how many already hold their own agency-level
    // record and would be skipped. Call before a `scope: "market"` save on any of the four
    // SET_BRIEF_*_V2 endpoints above. Takes `market_code` + `section` (not `agency_id`), and
    // requires the `full` content tier (permission bits 128 + 256).
    getBriefContentSavePrecheckV2: "GET_BRIEF_CONTENT_SAVE_PRECHECK_V2",
    // Royal Momentum - Notifications
    getNotifications: "GET_NOTIFICATIONS",
    getNotificationUnreadCount: "GET_NOTIFICATION_UNREAD_COUNT",
    markAllNotificationsRead: "MARK_ALL_NOTIFICATIONS_READ",
    markNotificationRead: "MARK_NOTIFICATION_READ",
    // V2 — `agency_id` replaces `travel_agency_uid`, and the recipient is addressed explicitly via
    // `recipient_user_uid` (falls back to `user_uid` server-side when omitted).
    // ⚠️ Asymmetric agency handling on the BE, so the payloads below are not interchangeable:
    //   - reads (getNotificationsV2 / DetailV2 / UnreadCountV2) resolve `agency_id` implicitly from
    //     `user_uid` when it is absent, so sending it is optional.
    //   - writes (markNotificationReadV2 / markAllNotificationsReadV2 / addNotificationV2 /
    //     deleteNotificationV2) have NO implicit fallback and hard-error with
    //     ERROR_MISSING_REQUIRED_PARAMETER, so `agency_id` must always be sent.
    // Neither variant expands agency_id to the Salesforce affiliate group.
    getNotificationsV2: "GET_NOTIFICATIONS_V2",
    getNotificationDetailV2: "GET_NOTIFICATION_DETAIL_V2",
    getNotificationUnreadCountV2: "GET_NOTIFICATION_UNREAD_COUNT_V2",
    markAllNotificationsReadV2: "MARK_ALL_NOTIFICATIONS_READ_V2",
    markNotificationReadV2: "MARK_NOTIFICATION_READ_V2",
    // Write paths with no UI yet: ADD is intended for internal/system/cron use, DELETE is a soft
    // delete (sets deleted_at). No V1 equivalent was ever defined in this file.
    addNotificationV2: "ADD_NOTIFICATION_V2",
    deleteNotificationV2: "DELETE_NOTIFICATION_V2",
    // Internal - User Management
    getAllUsers: "GET_ALL_USERS",
    adminUpdateUserFlags: "ADMIN_UPDATE_USER_FLAGS",
};
